import React from 'react'
import { motion } from 'framer-motion'
import { FiDroplet, FiShield, FiRepeat, FiUsers, FiZap, FiNavigation, FiArrowRight } from 'react-icons/fi'

const MissionsSection = () => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id)
    if (element) {
      const offset = 80
      const bodyRect = document.body.getBoundingClientRect().top
      const elementRect = element.getBoundingClientRect().top
      const elementPosition = elementRect - bodyRect
      const offsetPosition = elementPosition - offset
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' })
    }
  }

  const missions = [
    {
      title: 'Pavitra Jal: Lake Cleanup',
      description: 'Cleaning and beautifying our sweet waters (Pichola, Fateh Sagar) by hand. Let\'s remove all trash!',
      participants: '2.3K+',
      progress: 65,
      icon: FiDroplet,
      color: '#800000',
    },
    {
      title: 'Mewar Shaan: Heritage Care',
      description: 'Sweeping, washing, and keeping our historic temples, steps, and forts clean and shiny.',
      participants: '1.8K+',
      progress: 78,
      icon: FiShield,
      color: '#800000',
    },
    {
      title: 'Nirmal Nagar: Zero Garbage',
      description: 'Picking up plastics, wrappers, and waste from local parks, markets, and tourist spots.',
      participants: '3.4K+',
      progress: 42,
      icon: FiRepeat,
      color: '#800000',
    },
    {
      title: 'Shramdaan: Community Drives',
      description: 'Working together with neighbors and local groups for weekend society cleaning drives.',
      participants: '4.1K+',
      progress: 88,
      icon: FiUsers,
      color: '#800000',
    },
    {
      title: 'Yuva Shakti: Youth Volunteers',
      description: 'Connecting school and college youth to spread awareness and lead plastic-free zones.',
      participants: '2.7K+',
      progress: 55,
      icon: FiZap,
      color: '#800000',
    },
    {
      title: 'Swachh Marg: Clean Streets',
      description: 'Sweeping and keeping local footpaths, markets, and public squares sparkling clean.',
      participants: '3.5K+',
      progress: 71,
      icon: FiNavigation,
      color: '#800000',
    },
  ]

  return (
    <section id="missions" className="relative py-16 sm:py-24 md:py-32 px-4 sm:px-6 lg:px-8 bg-gray-50 overflow-hidden">
      <div className="absolute top-1/4 right-0 w-64 h-64 sm:w-96 sm:h-96 bg-accent-maroon/5 blur-[80px] sm:blur-[120px] rounded-full translate-x-1/2" />
      <div className="absolute bottom-1/4 left-0 w-64 h-64 sm:w-96 sm:h-96 bg-accent-maroon/3 blur-[80px] sm:blur-[120px] rounded-full -translate-x-1/2" />

      <div className="max-w-7xl mx-auto relative">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 sm:mb-16 md:mb-24 gap-4 sm:gap-6 md:gap-8">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl"
          >
            <h4 className="text-accent-maroon font-black tracking-[0.3em] uppercase text-xs mb-2 sm:mb-3 md:mb-4">🇮🇳 Ground Actions</h4>
            <h2 className="text-3xl sm:text-5xl md:text-7xl lg:text-8xl font-black font-grotesk tracking-tighter leading-none text-gray-800">
              JOIN THE <br />
              <span className="bg-gradient-to-r from-accent-maroon to-accent-maroonDark bg-clip-text text-transparent">
                SHRAMDAAN.</span>
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="text-gray-600 text-xs sm:text-sm md:text-base lg:text-lg font-medium max-w-sm md:text-right"
          >
            We have started 6 real, ground-level action areas in Udaipur. Choose your team and come work with us on the ground!
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
          {missions.map((mission, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -10 }}
              className="group relative bg-white/10 border border-gray-200 rounded-lg sm:rounded-xl lg:rounded-[2.5rem] p-6 sm:p-8 md:p-10 backdrop-blur-md overflow-hidden transition-all duration-500 hover:border-accent-maroon/30"
            >
              <div className="absolute -top-20 -right-20 w-40 h-40 opacity-0 group-hover:opacity-20 transition-opacity duration-500 rounded-full blur-[60px]" style={{ backgroundColor: mission.color }} />

              <div className="relative z-10">
                <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-lg sm:rounded-xl lg:rounded-2xl flex items-center justify-center mb-4 sm:mb-6 md:mb-8 border transition-all duration-500 group-hover:scale-110" style={{ backgroundColor: `${mission.color}10`, borderColor: `${mission.color}30`, color: mission.color }}>
                  <mission.icon size={24} />
                </div>

                <h3 className="text-lg sm:text-xl md:text-2xl font-black text-gray-800 mb-3 sm:mb-4 tracking-tight">{mission.title}</h3>
                <p className="text-gray-600 font-medium text-xs sm:text-sm leading-relaxed mb-6 sm:mb-8 md:mb-10">
                  {mission.description}
                </p>

                <div className="space-y-2 sm:space-y-3 mb-6 sm:mb-8 md:mb-10">
                  <div className="flex justify-between items-end">
                    <span className="text-[8px] sm:text-[10px] font-black text-gray-500 uppercase tracking-widest">Efficiency</span>
                    <span className="text-lg sm:text-xl font-black text-gray-800 leading-none">{mission.progress}%</span>
                  </div>
                  <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: `${mission.progress}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 1.5, ease: "easeOut" }}
                      className="h-full rounded-full"
                      style={{ backgroundColor: mission.color }}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 sm:pt-6 border-t border-gray-200">
                  <div className="flex flex-col">
                    <span className="text-[8px] sm:text-[10px] font-black text-gray-500 uppercase tracking-widest">Personnel</span>
                    <span className="text-gray-800 font-black text-sm sm:text-base">{mission.participants}</span>
                  </div>
                  <motion.button whileHover={{ x: 5 }} onClick={() => scrollToSection('volunteers')} className="flex items-center gap-1.5 sm:gap-2 text-accent-maroon font-black text-[10px] sm:text-xs uppercase tracking-widest group-hover:text-accent-maroonDark transition-colors">
                    <span>Deploy</span>
                    <FiArrowRight size={14} />
                  </motion.button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

export default MissionsSection